<template>
  <div>
    <v-dialog
      v-model="shouldShowDialog"
      :width="computedWidth"
      :persistent="shouldShowPostsScreen"

      scrollable

      @click:outside="emitClose()"
      @keydown.esc="emitClose()"
    >
      <v-card :loading="isLoading">
        <!-- show the popup for campaign name and date range -->
        <template v-if="!shouldShowPostsScreen">
          <v-card-text class="pt-4">
            <!-- show inputs for name and date range selectors -->
            <v-row class="mt-0">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.name"
                  label="Campaign Name"
                  outlined
                  :hide-details="!$v.formData.name.$anyError"
                  dense
                  :error-messages="$v.formData.name.$anyError ? ['Campaign name is required'] : null"
                  @blur="$v.formData.name.$touch"
                />
              </v-col>

              <v-col cols="12" md="6">
                <date-range-picker
                  v-model="formData.dateRange"
                  :hide-details="!$v.formData.dateRange.$anyError"
                  :error-messages="$v.formData.dateRange.$anyError ? ['Please select a date range'] : null"
                  @blur="$v.formData.dateRange.$touch"
                  label="Campaign Duration"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn depressed color="primary" @click="goToNextStep">
              Next
              <v-icon right>navigate_next</v-icon>
            </v-btn>
          </v-card-actions>
        </template>

        <!-- show the second step of this form -->
        <template v-else>
          <v-card-title>
            {{ formData.name || "Campaign Title" }}
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pt-6 campaign-form">
            <v-row v-for="(item, index) in formData.posts" :key="item.id" class="mb-3">
              <v-col
                cols="12"
                md="6"
                lg="4"
                class="d-flex"
              >
                <!-- <v-checkbox
                  v-model="item.isSelected"
                  class="mt-2 pt-0"
                ></v-checkbox> -->
                <v-text-field
                  v-model="item.username"
                  label="Username (Without @)"
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
                lg="4"
              >
                <v-text-field
                  v-model="item.postLink"
                  label="Post URL"
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
                lg="3"
              >
                <v-text-field
                  v-model="item.kpi"
                  label="Post KPI"
                  type="number"
                  hide-details
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                md="6"
                lg="1"
                class="d-flex justify-end"
              >
                <!-- to add a new item in array -->
                <!-- only show this if it's the last item in posts array -->
                <v-btn
                  v-if="index === formData.posts.length - 1"
                  fab
                  dark
                  small
                  depressed
                  color="primary"
                  @click="addNewRow"
                  :disabled="isLoading"
                >
                  <v-icon dark>add</v-icon>
                </v-btn>

                <!-- to remove this item from array -->
                <v-btn
                  fab
                  dark
                  small
                  depressed
                  color="primary"
                  class="ml-2"
                  @click="deleteRow(item)"
                  :disabled="isLoading"
                >
                  <v-icon dark>delete_outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <div
              v-if="$v.formData.posts.$anyError"
              class="red--text"
            >
              All inputs are required.
            </div>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>

            <v-btn
              text
              color="primary"
              @click="emitClose(true)"
              :disabled="isLoading"
            >Cancel</v-btn>

            <v-btn
              depressed
              color="primary"
              @click="goToNextStep"
              :disabled="isLoading"
            >
              Save
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { required, minLength, url, helpers } from "vuelidate/lib/validators"

const DateRangePicker = () => import("@/blocks/common/form/DateRangePicker.vue")

const defaultPostRow = {
  id: Symbol(),
  isSelected: false,
  username: "",
  postLink: "",
  kpi: ""
}

const initialFormData = () => ({
  name: "",
  dateRange: null,

  // filled in second screen of the form
  posts: [ { ...defaultPostRow, id: Symbol() } ]
})

export default {
  name: "CampaignForm",

  props: {
    isLoading: {
      type: Boolean,
      required: true
    },

    defaultData: {
      type: Object,
      required: false,
      default: null
    }
  },

  components: {
    DateRangePicker
  },

  data: () => ({
    // always set to true
    shouldShowDialog: true,

    // initiate with it set to false
    shouldShowPostsScreen: false,

    formData: initialFormData()
  }),

  computed: {
    computedWidth() {
      return this.shouldShowPostsScreen ? 1200 : 600
    }
  },

  validations: {
    formData: {
      name: {
        required,
      },

      dateRange: {
        required
      },

      posts: {
        required,
        minLength: minLength(1),
        $each: {
          username: {
            required,
            regex: helpers.regex("regex", /^[a-z0-9_]{2,}$/i)
          },

          postLink: {
            url,
            required,
            minLength: minLength(5),
          },

          kpi: {
            required
          }
        }
      }
    }
  },

  methods: {
    /**
     * Moves the form to the next step
    */
    async goToNextStep() {
      // if on first step, go to the second one
      if (!this.shouldShowPostsScreen) {
        // validate the form
        await this.$v.formData.name.$touch()
        await this.$v.formData.dateRange.$touch()

        if (!this.$v.formData.name.$anyError && !this.$v.formData.dateRange.$anyError) {
          this.shouldShowPostsScreen = true
        }

        return
      }

      // if that's not the case, go to the details page
      // then redirect to campaign details page
      // also show a progress bar for a second
      if (this.shouldShowPostsScreen) {
        await this.$v.formData.posts.$touch()

        if (!this.$v.formData.posts.$anyError) {
          // if everything's alright, emit save event
          this.$emit("submit", this.formData)
        }
      }
    },

    // emit the event to parent container
    emitClose(forceExit = false) {
      if (this.shouldShowPostsScreen && !forceExit) return

      this.$emit('close')

      // also reset the validations
      this.$v.$reset()

      // reset the formDate inputs
      this.formData = initialFormData()
   },

    // push an instance of user object to form array
    addNewRow() {
      this.formData.posts.push({ ...defaultPostRow, id: Symbol() })

      // once done, scroll to the bottom
      const el = document.querySelector(".campaign-form")
      if (el) {
        window.setTimeout(() => {
          el.scrollBy({ left: 0, top: el.scrollHeight, behavior: 'smooth' })
        }, 50)
      }
    },

    // find and delete the row from posts list
    deleteRow(item) {
      // if there's only one item, don't delete it
      if (this.formData.posts.length === 1) return

      // otherwise find the index
      const index = this.formData.posts.findIndex((search) => search.id === item.id)

      // if index is not negative 1, hence it exists
      if (index === -1) return

      // now splice it from the array
      this.formData.posts.splice(index, 1)
    }
  },

  mounted() {
    // if default data has been passed, then it means it's in edit state
    if (this.defaultData) {
      this.formData = {
        name: this.defaultData.name,
        dateRange: [this.defaultData.start_at, this.defaultData.end_at],

        // filled in second screen of the form
        posts: this.defaultData.posts
          .map((item) => ({
            id: item.id,
            username: item.user_id,
            postLink: item.post_url,
            kpi: item.post_kpi
          }))
      }

      // skip to the 2nd form screen
      this.shouldShowPostsScreen = true
    }
  }
}
</script>
